"use client"

import { toast } from "sonner"

type Message<T> = string | ((value: T) => string)

type ValidationError = {
  field: string
  message: string
}

export type ServerActionError = {
  error?: string
  errorMessage?: string
  status?: number
  success?: false
  validationErrors?: Record<string, string | string[]> | ValidationError[]
}

export type WithQueryHandlingOptions<T> = {
  loading?: string
  showLoadingToast?: boolean
  success?: Message<T>
  showSuccessToast?: boolean
  error?: Message<unknown>
  showErrorToast?: boolean
  notificationDuration?: number
  setErrors?: (errors: Record<string, unknown>) => void
}

/**
 * Executes a server-action promise with a loading, success, or error toast.
 *
 * @example
 * await withQueryHandling(updateProfileAction(input), {
 *   loading: "Saving profile...",
 *   success: "Profile saved",
 * })
 */
export async function withQueryHandling<T>(
  promise: Promise<T>,
  options: WithQueryHandlingOptions<T> = {},
): Promise<T> {
  const toastId =
    options.showLoadingToast === false
      ? null
      : toast.loading(options.loading ?? "Loading...", {
          duration: options.notificationDuration,
        })

  try {
    const result = await promise
    const actionError = getServerActionError(result)

    if (actionError) {
      setValidationErrors(actionError, options.setErrors)
      throw new Error(getErrorMessage(actionError))
    }

    if (options.showSuccessToast !== false) {
      toast.success(
        resolveMessage(options.success, result, "Completed"),
        getToastOptions(toastId, options.notificationDuration),
      )
    } else if (toastId !== null) {
      toast.dismiss(toastId)
    }

    return result
  } catch (error) {
    if (options.showErrorToast === false) {
      if (toastId !== null) toast.dismiss(toastId)
    } else {
      toast.error(
        resolveMessage(options.error, error, getErrorMessage(error)),
        getToastOptions(toastId, options.notificationDuration),
      )
    }

    throw error
  }
}

function getToastOptions(
  toastId: string | number | null,
  duration: number | undefined,
) {
  if (toastId === null) return { duration }
  return { id: toastId, duration }
}

export function getServerActionError(value: unknown): ServerActionError | null {
  if (!value || typeof value !== "object") return null

  const result = value as ServerActionError
  if (typeof result.errorMessage === "string" && typeof result.status === "number") {
    return result
  }

  if (result.success === false && typeof result.error === "string") {
    return result
  }

  return typeof result.error === "string" ? result : null
}

export function isServerActionError(value: unknown): value is ServerActionError {
  return getServerActionError(value) !== null
}

function setValidationErrors(
  error: ServerActionError,
  setErrors: WithQueryHandlingOptions<unknown>["setErrors"],
) {
  if (!error.validationErrors || !setErrors) return

  const flatErrors = Array.isArray(error.validationErrors)
    ? Object.fromEntries(error.validationErrors.map(({ field, message }) => [field, message]))
    : Object.fromEntries(
      Object.entries(error.validationErrors).map(([field, messages]) => [
        field,
        Array.isArray(messages) ? messages[0] : messages,
      ]),
    )

  setErrors(toNestedErrors(flatErrors))
}

function toNestedErrors(flatErrors: Record<string, string>): Record<string, unknown> {
  return Object.entries(flatErrors).reduce<Record<string, unknown>>((errors, [path, message]) => {
    setNestedError(errors, path, message)
    return errors
  }, {})
}

function setNestedError(target: Record<string, unknown>, path: string, message: string) {
  const segments = path.split(".").filter(Boolean)
  if (segments.length === 0) return

  let current: Record<string, unknown> = target
  for (const segment of segments.slice(0, -1)) {
    const existing = current[segment]
    if (!existing || typeof existing !== "object" || Array.isArray(existing)) {
      current[segment] = {}
    }
    current = current[segment] as Record<string, unknown>
  }

  current[segments[segments.length - 1]] = message
}

function resolveMessage<T>(message: Message<T> | undefined, value: T, fallback: string) {
  if (!message) return fallback
  return typeof message === "function" ? message(value) : message
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message
  if (error && typeof error === "object") {
    const result = error as ServerActionError
    if (typeof result.errorMessage === "string") return result.errorMessage
    if (typeof result.error === "string") return result.error
  }

  return "Something went wrong"
}
